'use client'
import { SidebarItems } from "@/components/sidebar-items";
import type { NavItem } from "@/components/sidebar";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
// import { Locale, PathInto, translate } from "@/types";
import { SidebarContent, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu } from "@/components/ui/sidebar";
import { SidebarPrincipalLabel } from "@/components/sidebar-principal-table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SidebarSearch } from "@/components/sidebar-search";
import { Role } from "@prisma/client";
import { useI18n } from "@/contexts/lang-context";
import { DotPathsFor } from "@/lib/i18n/init";


interface SidebarSearchedItemsProps {
  items: NavItem[],
  docs: NavItem & {
    children: NavItem[]
  }
  userId?: string
}

const transformHref = (href: string, replacements: Record<string, string | undefined>) => {
  Object.keys(replacements).forEach(key => {
    href = href.replace(`{${key}}`, replacements[key as keyof typeof replacements]!)
  })
  return href
}

const mapItems = (items: NavItem[], hasRole: (role: Role) => boolean, userId?: string) => {
  const replacements = { 'user': userId }
  return items
    .map(item => {
      if ('href' in item) {
        const toReturn: {
          href: string;
          title: DotPathsFor;
          icon: React.ReactNode;
          role: Role;
          children?: NavItem[];
          shouldTranslate: boolean
          open?: boolean
        } = {
          ...item,
          href: transformHref(item.href, replacements)
        }
        if ('children' in item && item.children) {
          toReturn.children = item.children
            .map(child => {
              if ('href' in child) {
                return {
                  ...child,
                  href: transformHref(child.href, replacements)
                }
              }
              return child
            })
            .filter(child => hasRole(child.role))
        }
        return toReturn
      }
      return item
    }).filter(item => hasRole(item.role))
}

export function SidebarSearchedItems({ items, docs, userId }: SidebarSearchedItemsProps) {
  const { t } = useI18n()
  const { hasRole, user } = useAuth()
  const [filter, setFilter] = useState<NavItem[]>([...mapItems([...items, docs], hasRole, userId)])
  const [search, setSearch] = useState<string>()

  function onSearch(search?: string) {
    if (search) {
      const f = docs.children.filter(child => {
        if (child.title === 'sidebar.documentation.create') return true
        if (child.shouldTranslate) {
          return t(child.title).toLowerCase().includes(search.toLowerCase())
        } else {
          return child.title.toLowerCase().includes(search.toLowerCase())
        }
      }).map(d => ({
        ...d,
        open: true
      }))
      const doc = {
        ...docs,
        children: f,
        open: true
      }

      setFilter([...mapItems([...items, doc], hasRole, userId)])
    } else {
      setFilter([...mapItems([...items, docs], hasRole, userId)])
    }
    setSearch(search)
  }

  useEffect(() => {
    onSearch(search)
  }, [user])

  return (
    <>
      <SidebarHeader>
        <SidebarGroup className="py-0 group-data-[collapsible=icon]:hidden">
          <SidebarGroupContent>
            <SidebarSearch onChangeSearch={onSearch} />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarHeader>
      <SidebarContent>
        <ScrollArea className="h-full max-w-60">
          <SidebarGroup>
            <SidebarPrincipalLabel />
            <SidebarMenu className="w-full">
              <SidebarItems items={filter} />
            </SidebarMenu>
          </SidebarGroup>
        </ScrollArea>
      </SidebarContent>
    </>
  )
}